import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, AlertTriangle, FileText, LogOut, ArrowRight } from "lucide-react";
import spApi, { getSpToken, setSpToken } from "@/lib/spApi";

const STATUS = {
  pending: { icon: Clock, color: "#EF9F27", title: "Under review", msg: "Thanks — your application is under review. We'll notify you by email once verified. This usually takes a few business days." },
  verified: { icon: CheckCircle2, color: "#22C55E", title: "Verified", msg: "You're verified and visible to patients. Open your portal to manage assigned cases." },
  rejected: { icon: XCircle, color: "#EF4444", title: "Not approved", msg: "Your application was not approved. You can update your details and resubmit." },
  expired: { icon: AlertTriangle, color: "#EF9F27", title: "Licence expired", msg: "Your licence appears to have expired. Update your licence details for re-verification before you can receive cases." },
};

export default function SpecialistStatus() {
  const navigate = useNavigate();
  const [sp, setSp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getSpToken()) {
      navigate("/specialist/login", { replace: true });
      return;
    }
    spApi.get("/specialists/me")
      .then(({ data }) => setSp(data))
      .catch(() => navigate("/specialist/login", { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    setSpToken(null);
    navigate("/specialist/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="care-scope min-h-screen w-full flex justify-center bg-black">
        <div className="w-full max-w-[430px] min-h-screen bg-[#111111] p-6">
          <div className="h-40 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }
  if (!sp) return null;

  const cfg = STATUS[sp.verification_status] || STATUS.pending;
  const Icon = cfg.icon;

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#111111] flex flex-col px-6 py-8">
        <div className="flex items-center justify-between">
          <h1 className="font-head text-xl font-bold text-[#F8FAFC]">Your application</h1>
          <button data-testid="sp-status-logout" onClick={logout} className="text-[#A1A1AA] flex items-center gap-1 text-sm min-h-[44px]">
            <LogOut size={16} /> Sign out
          </button>
        </div>

        <div data-testid="sp-status-card" className="mt-5 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ backgroundColor: cfg.color + "22", color: cfg.color }}>
            <Icon size={28} />
          </div>
          <h2 className="font-head text-xl font-bold text-[#F8FAFC]" data-testid="sp-status-label">{cfg.title}</h2>
          <p className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{cfg.msg}</p>
          {sp.verification_status === "rejected" && sp.verification_notes && (
            <p className="mt-3 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-3" data-testid="sp-reject-notes">
              {sp.verification_notes}
            </p>
          )}
          {sp.verification_status === "verified" && (
            <button data-testid="sp-open-portal" onClick={() => navigate("/specialist/portal")}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-5 py-3 min-h-[44px]">
              Open portal <ArrowRight size={16} />
            </button>
          )}
        </div>

        <div className="mt-5 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5">
          <h3 className="font-head font-bold text-[#F8FAFC] mb-2">Application details</h3>
          <div className="text-sm text-[#E5E5E5] space-y-1">
            <p><span className="text-[#A1A1AA]">Name:</span> {sp.full_name}</p>
            <p><span className="text-[#A1A1AA]">Specialty:</span> {sp.specialty}</p>
            <p><span className="text-[#A1A1AA]">Country:</span> {sp.country}</p>
            <p><span className="text-[#A1A1AA]">Licence:</span> {sp.licence_number} · {sp.licence_body}</p>
            <p><span className="text-[#A1A1AA]">Issued:</span> {sp.licence_issue_date} ({sp.licence_issue_country})</p>
            <p><span className="text-[#A1A1AA]">Expiry:</span> {sp.licence_no_expiry ? "No expiry" : sp.licence_expiry_date}</p>
            <p><span className="text-[#A1A1AA]">Fee:</span> {sp.review_fee} {sp.currency} · {sp.avg_turnaround_days} day turnaround</p>
          </div>
          {sp.documents?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#333333]">
              <p className="text-xs text-[#A1A1AA] mb-2">Uploaded documents (private)</p>
              <ul className="space-y-1">
                {sp.documents.map((d) => (
                  <li key={d.id} className="text-sm text-[#E5E5E5] flex items-center gap-2">
                    <FileText size={14} className="text-[#EF9F27]" /> {d.label || d.document_type} · {d.document_type.replace("_", " ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
